<template>
	<div
		class="absolute top-0 left-0 w-full pt-[28px] h-full bg-black z-[11001] bg-opacity-20 flex"
		@click.self="emit('close')"
	>
		<div
			class="mx-auto mb-auto mt-1 bg-body rounded border-2 border-primary"
		>
			<div class="flex items-center relative shadow-md ">
				<AppIcon
					icon="search"
					fixed-width
					class="absolute left-2 h-4"
				/>
				<input
					ref="searchInput"
					v-model="searchValue"
					v-maintain-focus
					class="p-3 pl-8 w-[640px] focus:outline-none bg-body-lighter"
					placeholder="Zoeken..."
					@keydown.down="inputEvents.onPressArrowDown"
					@keydown.up="inputEvents.onPressArrowUp"
					@keyup.escape="emit('close')"
					@keyup.enter.exact="inputEvents.onPressEnter"
					@keyup.ctrl.enter.exact="inputEvents.onPressCtrlEnter"
				>
				<KeyboardKey
					class="cursor-pointer select-none absolute right-4 text-xs hover:border-primary-600 hover:text-primary"
					@click="emit('close')"
				>
					Esc
				</KeyboardKey>
			</div>

			<div class="overflow-y-auto overflow-x-hidden max-h-[500px] py-2 text-body-content">
				<div
					v-if="results.length === 0"
					class="px-3 text-xs"
				>
					Geen resultaten gevonden voor '<span class="italic">
            {{ searchValue }} </span>'
				</div>
				<div
					v-for="({ item }, index) in results"
					:id="item.id"
					:key="item.id"
					:class="[
            'menu--item',
            { 'menu--item--focused': index === focusedIndex }
          ]"
					@click.prevent="openItem(item.action, undefined, item.title)"
				>
					<SidebarChildIcon
						:icon="item.icon"
						:compact="false"
						class="h-4 w-4"
					/>
					<span class="truncate">
            {{ item.title }}
          </span>
					<div
						class="group/bookmark ml-auto p-1"
						@click.prevent="addMenuBookmark(item.id)"
					>
						<AppIcon
							v-if="isMenuItemBookmarked(item.id)"
							class="group-hover/bookmark:hidden h-4 w-4"
							icon="fa fa-bookmark"
						/>
						<AppIcon
							v-if="isMenuItemBookmarked(item.id)"
							class="hidden group-hover/bookmark:inline-flex h-4 w-4"
							icon="fa fa-bookmark-slash"
							@click.stop="() => removeMenuBookmark(item.id)"
						/>
						<AppIcon
							v-if="!isMenuItemBookmarked(item.id)"
							class="group-hover/item:block h-4 w-4"
							icon="fa-regular fa-bookmark"
							@click.stop="() => addMenuBookmark(item.id)"
						/>
					</div>
				</div>
			</div>
			<div class="p-2 whitespace-nowrap bg-primary">
        <span class="text-xs">
          Gebruik <KeyboardKey>↑</KeyboardKey> <KeyboardKey>↓</KeyboardKey> voor
          navigatie, <KeyboardKey>Enter</KeyboardKey> voor openen,
          <KeyboardKey>Ctrl</KeyboardKey> +
          <KeyboardKey>Enter</KeyboardKey> voor aanmaken
        </span>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useFuse } from "@vueuse/integrations/useFuse";
import { vMaintainFocus } from "~/directives/vMaintainFocus";
import { useAppSidebarStore } from "~/stores/AppSidebar";

const searchInput = ref<HTMLInputElement | null>();
const searchValue = ref("");
const focusedIndex = ref(0);

const { Ctrl_B } = useMagicKeys();
const { isMenuItemBookmarked, addMenuBookmark, removeMenuBookmark } = useAppSidebarStore();
const { state } = storeToRefs(useAppSidebarStore());

const items = computed(() => state.value.flatMap(({ children }) => children));

const emit = defineEmits<{
	(e: "close"): void;
	(e: "openQBot"): void;
}>();

const { push } = useRouter();
const { results } = useFuse(searchValue, items, {
	fuseOptions: {
		keys: ["title"],
		threshold: 0.3,
	},
	matchAllWhenSearchEmpty: true,
});

const inputEvents = {
	onPressArrowDown: () => {
		// Wrap around if we reach the end
		focusedIndex.value = (focusedIndex.value + 1) % results.value.length;
	},
	onPressArrowUp: () => {
		// Wrap around if we reach the start
		focusedIndex.value = (focusedIndex.value - 1 + results.value.length) % results.value.length;
	},
	onPressEnter: () => {
		openItem(
			results.value[focusedIndex.value].item.action,
			results.value[focusedIndex.value].item.props,
			results.value[focusedIndex.value].item.title,
		);
	},
	onPressCtrlEnter: () => {
		openItem(`${results.value[focusedIndex.value].item.id}.create`);
	},
};

// `title` is only passed when the action is the menu item's own, so it actually names the page —
// not for the derived `<id>.create` action below.
function openItem(resourceKey: string, props?: Record<string, any>, title?: string) {
	if (props != undefined && Object.values(props).length > 0) {
		const query = new URLSearchParams(props).toString();
		push(`/${resourceKey.replace(".", "/")}` + `?${query}`, { title });
		emit("close");
	} else {
		push(`/${resourceKey.replace(".", "/")}`, { title });
		emit("close");
	}
}

whenever(Ctrl_B, () => {
	// Add or remove bookmark for focused item
	if (results.value.length === 0) return;

	const { id } = results.value[focusedIndex.value].item;
	if (isMenuItemBookmarked(id)) {
		removeMenuBookmark(id);
	} else {
		addMenuBookmark(id);
	}
});

watch(searchValue, () => {
	// Reset focused index when search value changes
	focusedIndex.value = 0;
});

watch(focusedIndex, (nv) => {
	// Scroll to focused item if it's not in view
	if (isFinite(nv) && results.value.length > 0) {
		document.getElementById(results.value?.[nv].item.id)?.scrollIntoView({
			behavior: "instant",
			block: "center",
		});
	}
});
</script>

<style scoped>
.menu--item {
	@apply pr-3 text-sm cursor-pointer hover:bg-body-darker flex flex-row items-center gap-3;
}

.menu--item--focused {
	@apply scale-[101%] bg-body-darker transition-transform;
}
</style>
